import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Menu, X, Activity } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/artists", label: "Artists" },
  { to: "/videos", label: "Videos" },
  { to: "/distribution", label: "Distribution" },
  { to: "/about", label: "About" },
];

export function PulseLogo({ size = 40 }) {
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <img
        src="/assets/logo-anim.webp"
        alt="Pulse Media"
        className="relative z-10 w-full h-full object-contain"
      />
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50"
        data-testid="site-header"
        style={{
          backdropFilter: scrolled ? "blur(18px)" : "blur(8px)",
          background: scrolled ? "rgba(8,8,10,0.75)" : "rgba(8,8,10,0.4)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
          transition: "all 0.3s ease",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <PulseLogo size={42} />
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="header-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{l.label}</span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute bottom-0 left-3 right-3 h-px"
                        style={{ background: "var(--accent)" }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="http://www.pulseportal.tv"
              target="_blank"
              rel="noopener noreferrer"
              className="glass-btn solid hidden sm:inline-flex"
              data-testid="header-stream-btn"
            >
              <Activity size={14} /> Portal Network
            </a>
            <button
              onClick={() => setOpen(!open)}
              className="md:hidden glass-btn ghost p-3"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-[72px] left-0 right-0 z-40 md:hidden"
            data-testid="mobile-nav-panel"
            style={{ background: "rgba(8,8,10,0.95)", backdropFilter: "blur(18px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="p-6 flex flex-col gap-2">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-base font-medium ${
                      isActive ? "bg-white/5 text-white" : "text-zinc-400"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
